/**
 * GiftBubble — per-type subcomponent for `ChatMessageType.gift` messages.
 *
 * Flutter source of truth:
 *  - Own variant: `currentUserMessageTypeGift` (`chat_screen.dart:1480-1564`).
 *  - Peer variant: `peerUserMessageTypeGift` (`chat_screen.dart:2594-2665`).
 *
 * Both variants are structurally identical — differences are:
 *  - Bubble background color (`AppColors.ownMessageColor` vs
 *    `AppColors.peerMessageColor`).
 *  - Title copy: own = "You sent a gift" (`chat.gift.youSent`); peer =
 *    "{peerName} sent you a gift" (`chat.gift.peerSent`).
 *  - Inner column / row alignment: own aligns to `end`, peer aligns to
 *    `start` (`chat_screen.dart:1499` / `:2598` and `:1509` / `:2611`).
 *  - Asymmetric corners: own's `bottomRight` collapses to 4.0 on the
 *    last-of-run bubble (`chat_screen.dart:1490-1493`); peer's `bottomLeft`
 *    collapses to 4.0 on the last-of-run bubble (mirror at `:2588-2591`).
 *    The dispatcher pre-computes `isLastOfRun`; the corner-radii helper from
 *    `MessageBubble.tsx` resolves the four CSS corners based on `isOwn` +
 *    `isLastOfRun`.
 *
 * Layout (interior, top → bottom):
 *  1. Title text — full-width, color white, fontSize 15
 *     (`chat_screen.dart:1501-1504` / `:2600-2606`).
 *  2. 4px top padding (`chat_screen.dart:1506` / `:2608`).
 *  3. Row:
 *       a. `"${giftInstances} x "` text — only when `giftInstances > 1`
 *          (`chat_screen.dart:1512-1518` / `:2613-2619`). Style:
 *          `AppStyles.chatMessageGiftSubTitleStyle` =
 *          `(textColor, fontSize: 13, weight: normal)`
 *          (`app_styles.dart:1032-1038`).
 *       b. Gift image — `chatMessage.giftPath!` (Env.giftsBucket prefix +
 *          `giftThumbnail`), 16×16 (`DimensionConstants.transactionGiftIconSize
 *          = 16`, `dimensions_constants.dart:81`).
 *       c. `" - ${MathUtils.roundDoubleTwoDecimals(charge!)} "` text — same
 *          subtitle style (`chat_screen.dart:1543-1547` / `:2644-2648`).
 *       d. Coin icon `assets/images/coins.png` 12×12 tinted with
 *          `AppColors.exclusive` (`chat_screen.dart:1548-1553` /
 *          `:2649-2654`; `DimensionConstants.notificationCoinIconSize = 12`).
 *
 * Tap behavior: Flutter's gift-bubble tap toggles `_messageTimeSeen[timestamp]`
 * (own variant: `chat_screen.dart:1460-1480`; peer variant: `:2567-2579`) — it
 * does NOT open a gift detail sheet. The own variant additionally retries the
 * send via `_retryFailedMessage(message)` when `!message.success`
 * (`chat_screen.dart:1465-1470`). Round one stubs the tap with
 * `InfoSheet.showNotImplementedYet` per the task plan's documented scope —
 * this is a *round-one scope deviation* from Flutter's timestamp-toggle /
 * retry behavior, not a placeholder for an unported "gift detail sheet."
 * The `onToggleTimestamp` and `onRetry` props on `BubbleSubcomponentProps`
 * are wired through to this subcomponent but intentionally unused in round
 * one; they exist so the gap is visible at the call site and so a future
 * pass can swap the stub for the real toggle/retry without changing the
 * dispatcher contract.
 *
 * Theme tokens (consistent with TextBubble — see Task 24):
 *  - Bubble background → own = `theme.chat.ownMessage`; peer = `theme.chat.peerMessage`.
 *  - Title text color   → `theme.white` (Flutter `AppColors.white`).
 *  - Subtitle text color → `theme.text.primary` (Flutter `AppColors.textColor`
 *    from `chatMessageGiftSubTitleStyle`).
 *  - Coin icon tint     → `theme.exclusive` (Flutter `AppColors.exclusive`).
 *
 * Responsive tokens (closest available on the project scale):
 *  - Title font size 15 → `responsive.font.lg` (16). Same TextBubble
 *    mapping; documented as a small UI deviation per `presentation.md`.
 *  - Subtitle font size 13 → `responsive.font.sm` (12). Closest available.
 *  - Bubble inner padding 10 → `responsive.spacing.sm` (8). Same TextBubble
 *    mapping.
 *  - Title→row gap 4 → `responsive.spacing.xs` (4) — exact.
 *  - Gift image 16×16 → `responsive.icon.small` (16) — exact.
 *  - Coin icon 12×12 → `responsive.icon.xs` (12) — exact.
 *  - Border radii → resolved by `getBubbleCornerRadii` (see
 *    `MessageBubble.tsx` for the full mapping).
 *
 * Gift image URL: Flutter's `ChatMessage.giftPath` getter prefixes
 * `Env.giftsBucket` to `giftThumbnail`. On the web the shared
 * `GiftUtils.getGiftPath(resourcePath)` domain helper plays the same role —
 * it takes only the string path, so it works uniformly whether the caller
 * holds a `ChatMessageEntity` (here), a DTO, or a `LatestLiveGiftInfoEntity`
 * (`VideoCallLastGift`), without converting the entity back into a DTO just to
 * read one field.
 */

import React from 'react';
import { ChatMessageType } from '@/domain/enums/chatMessageType';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { MathUtils } from '@/domain/utils/mathUtils';
import { GiftUtils } from '@/domain/utils/giftUtils';
import { ChatUtils } from '@/domain/utils/chatUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';
import {
  registerBubble,
  type BubbleSubcomponentProps,
} from '../../registry/bubbleRegistry';

export const GiftBubble: React.FC<BubbleSubcomponentProps> = ({
  message,
  peerUser,
  isOwn,
  isLastOfRun,
  screenKey,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const chat = l10n.chat;

  const corners = ChatUtils.getBubbleCornerRadii({
    isOwn,
    isLastOfRun,
    runMidRadius: responsive.borderRadius.lg,
    runEndRadius: responsive.borderRadius.sm,
  });

  // Title copy. Own → `chat.gift.youSent` (literal "You sent a gift"); peer →
  // `chat.gift.peerSent` with `{peerName}` replaced by the peer's display
  // name. Empty fallback for `peerUser` mirrors `ChatAppBar.tsx:144`.
  const peerName = peerUser?.name ?? '';
  const title = isOwn
    ? chat.gift.youSent
    : chat.gift.peerSent.replace('{peerName}', peerName);

  // Gift image URL via the shared `GiftUtils.getGiftPath` domain helper
  // (`env.GIFTS_BUCKET + giftThumbnail`). Guarded on a non-empty thumbnail.
  const giftImageUrl = message.giftThumbnail
    ? GiftUtils.getGiftPath(message.giftThumbnail)
    : '';

  // Charge text. Flutter uses `MathUtils.roundDoubleTwoDecimals(charge!)`;
  // the web equivalent is `MathUtils.roundTwoDecimals`. When `charge` is
  // missing fall back to `'0.00'` so the row still renders (matches the
  // `success === false` shape where charge may be undefined client-side).
  const chargeText =
    message.charge !== undefined && message.charge !== null
      ? MathUtils.roundTwoDecimals(message.charge)
      : '0.00';

  // Round one stub. Flutter's gift tap toggles `_messageTimeSeen[timestamp]`
  // (and on the own variant retries the send when `!message.success`); the
  // web port stubs both branches to `InfoSheet.showNotImplementedYet` per
  // the task plan's documented round-one scope. See the file header for
  // details. `onToggleTimestamp` / `onRetry` are intentionally not invoked
  // here so the deviation is visible at the call site.
  const handleTap = (): void => {
    void InfoSheet.showNotImplementedYet({ l10n, screenKey });
  };

  const backgroundColor = isOwn ? theme.chat.ownMessage : theme.chat.peerMessage;
  const innerAlign: 'flex-start' | 'flex-end' = isOwn ? 'flex-end' : 'flex-start';

  return (
    <button
      type="button"
      {...qaAttr({ id: `chat-bubble-gift-${message.documentId}` })}
      onClick={handleTap}
      style={{
        backgroundColor,
        border: 'none',
        borderTopLeftRadius: corners.topLeft,
        borderTopRightRadius: corners.topRight,
        borderBottomLeftRadius: corners.bottomLeft,
        borderBottomRightRadius: corners.bottomRight,
        paddingTop: responsive.spacing.sm,
        paddingBottom: responsive.spacing.sm,
        paddingLeft: responsive.spacing.sm,
        paddingRight: responsive.spacing.sm,
        display: 'flex',
        flexDirection: 'column',
        alignItems: innerAlign,
        textAlign: isOwn ? 'right' : 'left',
        cursor: 'pointer',
        maxWidth: '100%',
      }}
    >
      <span
        style={{
          color: theme.white,
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.regular,
        }}
      >
        {title}
      </span>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: innerAlign,
          marginTop: responsive.spacing.xs,
        }}
      >
        {message.giftInstances > 1 && (
          <span
            style={{
              color: theme.text.primary,
              fontSize: responsive.font.sm,
              fontWeight: responsive.fontWeight.regular,
            }}
          >
            {`${message.giftInstances} x `}
          </span>
        )}
        {giftImageUrl && (
          <CachedImage
            src={giftImageUrl}
            alt={message.giftName ?? ''}
            style={{
              width: responsive.icon.small,
              height: responsive.icon.small,
              objectFit: 'cover',
            }}
          />
        )}
        <span
          style={{
            color: theme.text.primary,
            fontSize: responsive.font.sm,
            fontWeight: responsive.fontWeight.regular,
          }}
        >
          {` - ${chargeText} `}
        </span>
        <AssetIcon
          path="/assets/images/coins.png"
          width={responsive.icon.xs}
          height={responsive.icon.xs}
          color={theme.exclusive}
        />
      </div>
    </button>
  );
};

// Register with the dispatcher so `MessageBubble` routes
// `ChatMessageType.gift` to this component. See `MessageBubble.tsx` for the
// registry contract.
registerBubble(ChatMessageType.gift, GiftBubble);
